import {
  ChangeEvent,
  FC,
  FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useQueryClient } from "react-query";
import { toast } from "react-toastify";
import styled from "styled-components";
import {
  BorderBox,
  DefaultLayout,
  FormButton,
  FormInput,
} from "../components/styles";
import useCreatePoint from "../hooks/useCreatePoint";
import usePointHistory from "../hooks/usePointHistory";
import { hideLoading } from "../Loading";
import { PaymentFeeType } from "../types/enum";
import { dateFormat, krwFormat, pointStatusFormat } from "../utils";

const Container = styled.div`
  margin-top: 40px;
`;

const DescriptionTitle = styled.div`
  font-size: 28px;
  font-weight: 500;
  text-align: center;
  margin-bottom: 40px;
`;
const SmallDescription = styled.div`
  font-size: 18px;
  font-family: normal;
  margin-bottom: 5px;

  @media (max-width: 880px) {
    font-size: 15px;
  }
`;
const InnerDescription = styled.div`
  border: 1px solid #d2d2d8;
  border-radius: 10px;
  padding: 10px 12px;
  font-family: normal;
  font-size: 15px;
  white-space: pre-wrap;

  ul {
    list-style: "ㆍ";
    padding-left: 10px;
  }
  li {
    padding-bottom: 10px;
  }

  @media (max-width: 880px) {
    font-size: 13px;
  }
`;
const AddFormContainer = styled.div`
  margin-top: 40px;
`;
const TotalPriceMoney = styled(DescriptionTitle)`
  margin-bottom: 20px;
`;
const AddPriceMoney = styled.div`
  padding-bottom: 4px;
  width: 300px;
  margin: 0 auto;
  border-bottom: 1px solid rgb(210, 210, 216);
  font-size: 40px;
  font-weight: 500;
  color: #000;
  text-align: center;
  position: relative;

  span {
    font-weight: bold;
    color: var(--main-color);
  }

  img {
    position: absolute;
    top: 50%;
    right: 5px;
    transform: translateY(-50%);
  }

  @media (max-width: 880px) {
    width: 100%;
    font-size: 30px;
  }
`;
const AddButtonContainer = styled.div`
  margin-top: 15px;
  display: flex;
  gap: 0 10px;
  button {
    font-size: 17px;
    padding: 15px 0;
    border: 1px solid rgb(210, 210, 216);
    transition: all 300ms ease 0s;
    background: #fff;
    flex: 1;
  }
  button:hover {
    background: rgb(238, 238, 238);
  }

  @media (max-width: 880px) {
    gap: 0 5px;
    button {
      font-size: 13px;
    }
  }
`;
const FormItemWrapper = styled.div`
  margin-top: 20px;

  div {
    font-weight: 500;
    font-size: 19px;
    margin-bottom: 5px;
  }

  @media (max-width: 880px) {
    div {
      font-size: 15px;
    }
  }
`;
const StyledFormInput = styled(FormInput)`
  font-family: normal;
  background: #fff;
  border: 1px solid rgb(230, 230, 230);
  border-radius: 4px;
  font-size: 16px;

  @media (max-width: 880px) {
    font-size: 13px;
  }
`;
const FeeButtonContainer = styled(AddButtonContainer)`
  button.active {
    border: 1px solid var(--main-color);
    color: var(--main-color);
  }
  button:hover {
    background: none;
  }
`;
const BackgroundContainer = styled.div`
  padding: 10px 0;

  background: #fcfcfc;
`;
const FormRow = styled.div`
  display: flex;
  gap: 0 20px;
  padding: 0 10px;
  > div {
    flex: 1;
    input {
      margin-top: 2px;
      width: 100%;
    }
  }
  & + & {
    margin-top: 8px;
  }
  @media (max-width: 880px) {
    font-size: 13px;
  }
`;
const StyledBorderBox = styled(BorderBox)`
  border: 1px solid #d2d2d8;

  @media (max-width: 880px) {
    border: none;
  }
`;
const AddHistory = styled(StyledBorderBox)`
  margin-top: 30px;

  table {
    width: 100%;
    text-align: center;
    font-size: 13px;
    border-top: 1px solid black;
    border-bottom: 1px solid black;
    border-collapse: collapse;
  }
  thead {
    background: var(--main-color);
    color: #fff;
  }
  tbody tr {
    border-top: 1px solid black;
  }
  td {
    padding: 8px;
    font-weight: normal;
  }
`;
const TableWrapper = styled.div`
  width: 100%;
  overflow: auto;
`;

const CompleteHeader = styled.div`
  padding: 40px 0px;
  color: rgb(255, 255, 255);
  font-size: 16px;
  font-weight: 500;
  margin-top: 20px;
  background: var(--main-color);
  text-align: center;
`;
const CompleteIconContent = styled.div`
  font-size: 25px;
  padding: 50px 0px;
  text-align: center;

  img {
    width: 40px;
    height: 40px;
    margin-bottom: 5px;
  }

  div {
    b {
      font-weight: 600;
    }
  }
`;
const CreateContainer = styled.div`
  hr {
    height: 0;
    border-top: 1px solid rgb(210, 210, 216);
    margin: 1em 0px;
  }
`;
const ContentRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 30px 0px;
  font-size: 13px;
  align-items: center;

  div {
    text-align: right;
    white-space: pre-wrap;
  }
`;

const CreateView: FC<{ value: { name: string; point: number } }> = ({
  value,
}) => {
  return (
    <CreateContainer>
      <CompleteHeader>
        아래 정보로 {krwFormat(value.point)}원 입금시 자동으로 충전됩니다
      </CompleteHeader>
      <CompleteIconContent>
        <img src="data:image/png;base64,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" />
        <div>
          <b>충전 신청</b> 완료
        </div>
      </CompleteIconContent>
      <hr />
      <ContentRow>
        <span>입금계좌정보</span>
        <div>
          거래은행 : 카카오뱅크{"\n"}
          계좌번호 : 3333-23-2875976{"\n"}
          예금주 : 나현채{"\n"}
        </div>
      </ContentRow>
      <hr />
      <ContentRow>
        <span>입금자명</span>
        <span>{value.name}</span>
      </ContentRow>
      <hr />
      <ContentRow>
        <span>결제금액</span>
        <span>{krwFormat(value.point)}</span>
      </ContentRow>
    </CreateContainer>
  );
};

const UnCreateView: FC<{
  onCreate: (value: { point: number; name: string }) => void;
}> = ({ onCreate }) => {
  const queryClient = useQueryClient();
  const [feeType, setFeeType] = useState<PaymentFeeType>(PaymentFeeType.NONE);
  const [feeValue, setFeeValue] = useState<Object>({});
  const [money, setMoney] = useState<number>(0);
  const [name, setName] = useState<string>("");

  const { mutate: createPointApi, isLoading: isCreateLoading } = useCreatePoint(
    {
      onSuccess: () => {
        queryClient.refetchQueries("point-history");
        toast.success("아래 정보를 확인해주세요.");
        onCreate({ name, point: money });
      },
      onError: () => {
        toast.error("오류가 발생했습니다 잠시 후 시도해 주세요.");
      },
    }
  );

  const changeFeeTypeCallback = useCallback((value: PaymentFeeType) => {
    return () => {
      setFeeType(value);
    };
  }, []);
  const addMoneyCallback = useCallback((value: number) => {
    return () => {
      setMoney((prev) => prev + value);
    };
  }, []);
  const resetMoney = useCallback(() => {
    setMoney(0);
  }, []);

  const changeName = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setName(e.target.value);
  }, []);

  const submitHandler = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (isCreateLoading) return;
    if (name.length === 0) {
      toast.error("이름을 입력해 주세요.");
      return;
    }

    if (money === 0 || money % 5000 !== 0) {
      toast.error("5000원 단위로 충전 가능합니다");
      return;
    }
    createPointApi({ name, point: money, feeValue, feeType });
  };

  const changeFeeValue = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFeeValue((prev) => ({
      ...prev,
      [name]: value,
    }));
  }, []);

  useEffect(() => {
    setFeeValue({});
  }, [feeType]);

  return (
    <form onSubmit={submitHandler}>
      <DescriptionTitle>포인트 충전</DescriptionTitle>
      <SmallDescription>유의사항</SmallDescription>
      <InnerDescription>
        <ul>
          <li>
            결제금액과 입금자명이 정확히 일치해야 합니다.{"\n"}
            (일치하지 않을 경우, 전산 과정에서 누락될 수 있습니다.)
          </li>
          <li>
            입금 완료 후 평균 1~10분 이내 자동으로 잔액이 충전됩니다.
            {"\n"}
            (충전이 되지 않을 경우, 1:1문의 주세요.)
          </li>
          <li>3시간이 지나면 충전이 자동으로 취소됩니다.</li>
        </ul>
      </InnerDescription>
      <AddFormContainer>
        <TotalPriceMoney>충전금액</TotalPriceMoney>
        <AddPriceMoney>
          <div>
            <span>{krwFormat(money)}</span>원
            <img
              onClick={resetMoney}
              src="data:image/png;base64,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"
            />
          </div>
        </AddPriceMoney>
        <AddButtonContainer>
          <button type="button" onClick={addMoneyCallback(5000)}>
            + 5천원
          </button>
          <button type="button" onClick={addMoneyCallback(10000)}>
            + 1만원
          </button>
          <button type="button" onClick={addMoneyCallback(50000)}>
            + 5만원
          </button>
          <button type="button" onClick={addMoneyCallback(100000)}>
            + 10만원
          </button>
        </AddButtonContainer>
        <FormItemWrapper>
          <div>입금자명</div>
          <StyledFormInput
            onChange={changeName}
            value={name}
            placeholder="결제금액과 입금자명이 일치해야 정상 충전됩니다"
          />
        </FormItemWrapper>
        <FormItemWrapper>
          <div>계산서 / 영수증</div>
          <FeeButtonContainer>
            <button
              type="button"
              onClick={changeFeeTypeCallback(PaymentFeeType.NONE)}
              className={`${feeType === PaymentFeeType.NONE ? "active" : ""}`}
            >
              신청안함
            </button>
            <button
              type="button"
              onClick={changeFeeTypeCallback(PaymentFeeType.FEE)}
              className={`${feeType === PaymentFeeType.FEE ? "active" : ""}`}
            >
              세금계산서
            </button>
            <button
              type="button"
              onClick={changeFeeTypeCallback(PaymentFeeType.CASH)}
              className={`${feeType === PaymentFeeType.CASH ? "active" : ""}`}
            >
              현금영수증
            </button>
          </FeeButtonContainer>
        </FormItemWrapper>
      </AddFormContainer>
      {feeType === PaymentFeeType.FEE ? (
        <BackgroundContainer>
          <FormRow>
            <div>
              <div>회사명</div>
              <StyledFormInput
                onChange={changeFeeValue}
                name="company"
                type="text"
              />
            </div>
            <div>
              <div>사업자등록번호</div>
              <StyledFormInput
                type="text"
                onChange={changeFeeValue}
                name="businessNumber"
              />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <div>대표자</div>
              <StyledFormInput
                type="text"
                onChange={changeFeeValue}
                name="leader"
              />
            </div>
            <div>
              <div>담당연락처</div>
              <StyledFormInput
                type="text"
                onChange={changeFeeValue}
                name="phone"
              />
            </div>
          </FormRow>
          <FormRow>
            <div>
              <div>메일주소</div>
              <StyledFormInput
                type="text"
                onChange={changeFeeValue}
                name="email"
              />
            </div>
          </FormRow>
        </BackgroundContainer>
      ) : (
        feeType === PaymentFeeType.CASH && (
          <BackgroundContainer>
            <FormRow>
              <div>
                <div>휴대폰</div>
                <StyledFormInput
                  type="text"
                  onChange={changeFeeValue}
                  name="phone"
                />
              </div>
            </FormRow>
          </BackgroundContainer>
        )
      )}
      <FormButton type="submit">{krwFormat(money)}원 결제하기</FormButton>
    </form>
  );
};
const PoingPage: FC = () => {
  const [createdInfo, setCreatedInfo] = useState<null | {
    point: number;
    name: string;
  }>(null);

  const { data: pointHistory = [] } = usePointHistory({
    onSuccess: () => {
      hideLoading();
    },
  });
  const onCreate = useCallback((value: { point: number; name: string }) => {
    setCreatedInfo(value);
  }, []);

  return (
    <Container>
      <DefaultLayout>
        <StyledBorderBox>
          {createdInfo === null ? (
            <UnCreateView onCreate={onCreate} />
          ) : (
            <CreateView value={createdInfo} />
          )}
        </StyledBorderBox>
        <AddHistory>
          <DescriptionTitle>충전 내역</DescriptionTitle>
          <TableWrapper>
            <table>
              <thead>
                <tr>
                  <td>충전번호</td>
                  <td>요청일시</td>
                  <td>충전금액</td>
                  <td>충전상태</td>
                  <td>수락/거절 시간</td>
                </tr>
              </thead>
              <tbody>
                {pointHistory.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{dateFormat(item.createdAt)}</td>
                    <td>{krwFormat(item.point)}</td>
                    <td>{pointStatusFormat(item.status)}</td>
                    <td>
                      {item.responseAt ? dateFormat(item.responseAt) : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>
        </AddHistory>
      </DefaultLayout>
    </Container>
  );
};

export default PoingPage;
